import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Building, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePurchaseOrders } from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { formatCurrency, formatDate, formatRut } from "~/utils/helpers";

import type { Supplier } from "~/types/purchasing";

interface SupplierOrderGroup {
  supplier: Supplier;
  count: number;
  totalClp: number;
  lastDate: string;
}

const PAGE_SIZE = 15;

export function ComprasProveedoresPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: orders = [], isLoading } = usePurchaseOrders();

  // Only the proveedores we actually order from — derived by grouping the POs
  // (avoids listing the full ~1.4k supplier master). CLP orders only for the
  // monto total, to avoid mixing currencies.
  const groups = useMemo<SupplierOrderGroup[]>(() => {
    const map = new Map<string, SupplierOrderGroup>();
    for (const order of orders) {
      if (!order.supplier) continue;
      const clpAmount = order.currency === "CLP" ? order.total : 0;
      const existing = map.get(order.supplier.id);
      if (existing) {
        existing.count += 1;
        existing.totalClp += clpAmount;
        if (order.issue_date > existing.lastDate) {
          existing.lastDate = order.issue_date;
        }
      } else {
        map.set(order.supplier.id, {
          supplier: order.supplier,
          count: 1,
          totalClp: clpAmount,
          lastDate: order.issue_date,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.lastDate < b.lastDate ? 1 : -1
    );
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.supplier.name.toLowerCase().includes(q) ||
        (g.supplier.rut ?? "").toLowerCase().includes(q)
    );
  }, [groups, search]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filtered,
    PAGE_SIZE
  );

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="Proveedores"
          description="Proveedores con órdenes de compra y su historial de OCs"
        />
        <Card>
          <CardContent className="p-0">
            <TableSkeleton rows={10} columns={5} />
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Proveedores"
        description="Proveedores con órdenes de compra y su historial de OCs"
      />

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {groups.length} proveedores con OCs
            </p>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar proveedor / RUT..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <EmptyState
              icon={Building}
              title="Sin proveedores con órdenes de compra"
              description="Cuando registres órdenes de compra, los proveedores aparecerán aquí."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead className="text-right">Nº de OCs</TableHead>
                    <TableHead className="text-right">
                      Monto total (CLP)
                    </TableHead>
                    <TableHead>Última OC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((g, index) => (
                    <TableRow
                      key={g.supplier.id}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                      onClick={() =>
                        navigate(
                          `/purchasing/compras/proveedores/${g.supplier.id}`
                        )
                      }
                    >
                      <TableCell className="font-medium">
                        {g.supplier.name}
                        {g.supplier.is_foreign && (
                          <Badge
                            variant="secondary"
                            className="ml-2 bg-gray-100 text-gray-700"
                          >
                            Extranjero
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {g.supplier.rut ? formatRut(g.supplier.rut) : "-"}
                      </TableCell>
                      <TableCell className="text-right">{g.count}</TableCell>
                      <TableCell className="text-right font-medium">
                        ${formatCurrency(g.totalClp)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(g.lastDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="border-t p-4">
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  totalItems={total}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                  itemLabel="proveedores"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
